import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UISectionWrapper, {
  UISectionBody,
  UISectionHeader,
} from '@/components/shared/UISectionWrapper';
import UISimpleTable from '@/components/shared/UISimpleTable';
import UIPaginator from '@/components/shared/UIPaginator';
import UIButton from '@/components/shared/UIButton';
import useGetVacantUnits from '@/hooks/useGetVacantUnits';
import moment from 'moment';
import type { GetVacantUnitsDashboardTableRow } from '@/types/responses';
import UIProperty from '@/components/shared/UIProperty/UIProperty';
import './VacantUnitsSection.scss';
import { VacantUnitFromApi } from '@/types/entities';

const baseClass = 'DashboardPage__VacantUnitsSection';

interface VacantUnitsSectionProps {
  className?: string;
  hideViewAllButton?: boolean;
  disablePagination?: boolean;
  hideHeader?: boolean;
}

const VacantUnitsSection: React.FC<VacantUnitsSectionProps> = ({
  className = '',
  hideViewAllButton = false,
  disablePagination = false,
  hideHeader = false,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { data: response, error, isPending } = useGetVacantUnits({
    page: disablePagination ? 1 : currentPage,
    limit: itemsPerPage,
  });

  const actualData = response?.data.data || [];
  const meta = response?.data.meta;

  const transformedData = actualData.map((item: VacantUnitFromApi) => ({
    id: item.id,
    number: item.number,
    rentPrice: item.rentPrice,
    vacateDate: item.vacateDate,
    vacantDays: item.vacantDays,
    address: {
      ...item.property.address,
      property: {
        id: item.property.id,
        name: item.property.name,
        type: item.property.type,
      },
    },
  }));

  // Колонки для таблиці
  const tableColumns = [
    {
      key: 'property',
      label: 'Property',
      width: '250px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => {
        const propertyName = row.address?.property?.name || 'Unknown Property';
        const propertyType = row.address?.property?.type;

        return (
          <UIProperty
            name={propertyName}
            type={propertyType as 'residential' | 'commercial' | 'mixed' | undefined}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/dashboard/properties/${row.address?.property?.id || row.id}`,
              );
            }}
          />
        );
      },
    },
    {
      key: 'address',
      label: 'Address',
      width: '250px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => {
        const addressString = row.address
          ? `${row.address.street || ''}, ${row.address.city || ''}`
              .replace(/^,\s*/, '')
              .replace(/,\s*$/, '')
          : '-';

        return <div className={`${baseClass}__address`}>{addressString}</div>;
      },
    },
    {
      key: 'unit',
      label: 'Unit',
      width: '100px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => {
        const unitNumber = row.number || '-';
        return (
          <div
            className={`${baseClass}__unit-link`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/dashboard/properties/${row.address?.property?.id || row.id}/units/${row.id}`,
              );
            }}
          >
            {unitNumber}
          </div>
        );
      },
    },
    {
      key: 'vacateDate',
      label: 'Vacate Date',
      width: '120px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => {
        const dateString = row.vacateDate
          ? moment(row.vacateDate).format('YYYY-MM-DD')
          : '-';
        return <div className={`${baseClass}__date`}>{dateString}</div>;
      },
    },
    {
      key: 'vacantDays',
      label: 'Vacant Days',
      width: '120px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => {
        const days = row.vacantDays;
        const isOverdue = days > 0;
        const daysString = isOverdue ? `+${days}` : `${days}`;

        return (
          <div
            className={`${baseClass}__days ${
              isOverdue ? `${baseClass}__days--overdue` : ''
            }`}
          >
            {daysString}
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '48px',
      sortable: false,
      sortDirection: null,
      showDivider: false,
      render: (value: any, row: GetVacantUnitsDashboardTableRow) => (
        <div
          className={`${baseClass}__actions`}
          onClick={(e) => {
            e.stopPropagation();
            handleActionsClick(row);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="3" r="1" fill="#637381" />
            <circle cx="8" cy="8" r="1" fill="#637381" />
            <circle cx="8" cy="13" r="1" fill="#637381" />
          </svg>
        </div>
      ),
    },
  ];

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    console.log('Sort:', columnKey, direction);
    // Тут можна додати логіку сортування
  };

  const handleRowClick = (row: GetVacantUnitsDashboardTableRow) => {
    navigate(`/dashboard/properties/${row.address.property.id}/units/${row.id}`);
  };

  const handleActionsClick = (row: GetVacantUnitsDashboardTableRow) => {
    console.log('Actions clicked for unit:', row.id);
    // Тут можна додати меню дій або модальне вікно
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewAll = () => {
    navigate('/dashboard/properties/vacant-units');
  };

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  if (error) {
    return (
      <UISectionWrapper className={containerClasses}>
        {!hideHeader && (
          <UISectionHeader>
            <div className={`${baseClass}__header`}>
              <Link to="/dashboard/properties/vacant-units" className={`${baseClass}__title-link`}>
                <h3 className={`${baseClass}__title`}>Vacant units</h3>
              </Link>
              {!hideViewAllButton && (
                <UIButton variant="primary" size="small" onClick={handleViewAll}>
                  View All
                </UIButton>
              )}
            </div>
          </UISectionHeader>
        )}
        <UISectionBody>
          <div className={`${baseClass}__error`}>
            Error loading vacant units: {error.message}
          </div>
        </UISectionBody>
      </UISectionWrapper>
    );
  }

  return (
    <UISectionWrapper className={containerClasses} spacing="tight">
      {!hideHeader && (
        <UISectionHeader>
          <div className={`${baseClass}__header`}>
            <Link to="/dashboard/properties/vacant-units" className={`${baseClass}__title-link`}>
              <h3 className={`${baseClass}__title`}>Vacant units</h3>
            </Link>
            {!hideViewAllButton && (
              <UIButton variant="primary" size="small" onClick={handleViewAll}>
                View All
              </UIButton>
            )}
          </div>
        </UISectionHeader>
      )}

      <UISectionBody>
        {isPending ? (
          <div className={`${baseClass}__loading`}>Loading...</div>
        ) : (
          <>
            <UISimpleTable
              data={transformedData}
              columns={tableColumns}
              className={`${baseClass}__table`}
              onSort={handleSort}
              onRowClick={handleRowClick}
            />

            {!disablePagination && meta && meta.totalPages > 1 && (
              <UIPaginator
                totalPages={meta.totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                className={`${baseClass}__navigation`}
              />
            )}
          </>
        )}
      </UISectionBody>
    </UISectionWrapper>
  );
};

export default VacantUnitsSection;
